import type { ChangelogEntry } from '../types';

/**
 * Changelog entries for the ATEK platform.
 * Add new entries at the top of the array (most recent first).
 *
 * Categories:
 * - feature: New functionality or capability
 * - improvement: Enhancement to existing functionality
 * - fix: Bug fix or correction
 */
export const changelogEntries: ChangelogEntry[] = [
  {
    id: '2026-10-30-qms-status-email-notifications',
    date: '2026-10-30',
    title: 'QMS status emails from the ticket form',
    description: 'Saving a new status inside a QMS ticket sends the same email notifications as moving it on the Kanban board. Saving without changing the status sends no status-change email.',
    category: 'fix',
  },
  {
    id: '2026-10-28-company-pulse',
    date: '2026-10-28',
    title: 'Company Pulse: our goals in view',
    description: 'See leadership-selected company goals, results against targets, and quarterly priorities in one shared view.',
    category: 'feature',
    details: [
      'Switch between week, month and quarter, with missing updates clearly identified.',
      'Bookmark the page or use the rotating hall display with automatic refresh.',
      'Leadership chooses which existing KPIs and company Rocks to share with employees.',
    ],
  },
  {
    id: '2026-10-28-subscription-calibration-quantities',
    date: '2026-10-28',
    title: 'Reliable calibration quantities at renewal',
    description: 'Cal Qty follows each linked sensor’s Calibration flag, including inherited settings from covered accounts. Editing the flag refreshes subscription counts and renewal previews.',
    category: 'fix',
    details: [
      'Split calibration lines keep their quantities, prices and discounts, with a warning to review the allocation before sending.',
      'A single calibration line uses the current count of included sensors.',
    ],
  },
  {
    id: '2026-10-26-eqms-edit-uploaded-documents',
    date: '2026-10-26',
    title: 'Edit uploaded documents in eQMS',
    description: 'Open an uploaded Word, PDF, image or text file as an editable working draft, or place the original under control as-is. The original file is preserved and remains linked to the new controlled document.',
    category: 'feature',
    details: [
      'Choose how to use the file immediately after uploading or selecting it from DMS',
      'PDFs and images open automatically once their extracted text is ready',
      'Review imported text, formatting and images before preparing the PDF and signing',
    ],
    image: '2026-10-26-eqms-edit-uploaded-documents.png',
  },
  {
    id: '2026-10-22-eqms-discard-draft',
    date: '2026-10-22',
    title: 'Discard eQMS drafts uploaded by mistake',
    description: 'Quality users can now discard a controlled document that is still a draft, for example a wrong upload or a document no longer pursued. It leaves the register and its control code can be reused.',
    category: 'feature',
    details: [
      'Open the draft in the eQMS register and choose "Discard draft"; a reason and your password signature are required',
      'Only drafts can be discarded: documents in review, approved, effective or obsolete keep their normal lifecycle, and an open revision of an effective document cannot be discarded',
      'Nothing is erased: the file, signatures and audit trail are kept as evidence, and the document shows who discarded it, when and why',
      'Discarded drafts are hidden from "All states" and the counts; pick "Discarded" in the state filter to find them',
      'The control code is freed, so the correct file can be registered under the same code',
    ],
    image: '2026-10-22-eqms-discard-draft.png',
  },
  {
    id: '2026-10-20-equipment-library',
    date: '2026-10-20',
    title: 'Equipment Library',
    description: 'A new Operations page to manage the fridges, freezers, ULTs and incubators ATEK installs on, with install guides, manuals and photos, plus a queue for reviewing models submitted by technicians.',
    category: 'feature',
    details: [
      'Install guide per model: cable port, probe and sensor positions, each marked verified or not',
      'Manufacturer manuals and photos stored privately, opened through short-lived links',
      'Review queue to approve, merge or reject technician-submitted models',
    ],
  },
  {
    id: '2026-10-20-delete-private-eqms-drafts',
    date: '2026-10-20',
    title: 'Delete Unneeded eQMS Working Drafts',
    description:
      'Remove an unregistered draft from your Command Center after confirming its name. Only its owner can delete it; concurrent edits require a fresh review, and preparation records and images are retained.',
    category: 'feature',
    image: '2026-10-20-delete-private-eqms-drafts.png',
  },
  {
    id: '2026-10-19-eqms-update-requests',
    date: '2026-10-19',
    title: 'Request Updates to Controlled Documents',
    description:
      'Any employee can ask for an update to an Effective controlled document, with a written reason. The document owner is emailed and keeps every request in view on the eQMS Command Center until it is addressed, parked for the next major update, declined or done.',
    category: 'feature',
    details: [
      '"Request an update" is available from the training reader and from the eQMS document view.',
      'Only the document owner sees the requests for their documents; each requester sees their own and the owner\'s decision.',
      'Owners decide from the Command Center inbox, the "Update requests" tab of the document or the register badge. Declining requires a note for the requester.',
      '"Address now" opens the revision of a document written in eQMS, and the requests close automatically when that revision becomes Effective.',
      'Parked requests are offered again when the owner starts a new revision, and stay visible in the editor while they work.',
    ],
    image: '2026-10-19-eqms-update-requests.png',
  },
  {
    id: '2026-10-19-eqms-import-effective',
    date: '2026-10-19',
    title: 'Import Already-Effective Documents into eQMS',
    description:
      'When Cindy or Ilyas uploads a document that was already effective in a previous system, they can check "Import as already effective" to place it under control directly as Effective (v1.0), without the review and approval steps.',
    category: 'feature',
    details: [
      'Only cindy@atek.io and ilyas@atek.io see the option; the server rejects it for every other account.',
      'A written reason for bypassing review and approval is required before signing.',
      'The import is one signed release (password re-entry, Uncontrolled → Effective) recorded in the signatures and audit trail.',
      'Cindy can also select "No training required" for an imported document; for Ilyas\'s imports, and otherwise, training is assigned as for any release.',
      'Documents that go through the normal workflow are unchanged.',
    ],
    image: '2026-10-19-eqms-import-effective.png',
  },
  {
    id: '2026-10-13-notification-schedule-morning-times',
    date: '2026-10-13',
    title: 'Notification Schedules Keep Morning Times',
    description:
      'Notification schedule fields retain morning times such as 07:00 when you leave the input, and blank or malformed times cannot be saved or silently changed to midnight.',
    category: 'fix',
    legacyFrontend: true,
  },
  {
    id: '2026-10-08-eqms-flexible-documents-and-forms',
    date: '2026-10-08',
    title: 'Flexible Procedure Editing and Employee Forms',
    description:
      'Write procedures in English, French or both, with independent headers, adjustable formatting and visible pages. Employees can start published forms directly, save unfinished answers and complete their records with their own signature.',
    category: 'improvement',
    details: [
      'Distinct headings, aligned text, subsection numbering and adjustable table colours, columns, rows and spacing.',
      'Bilingual grouped fields, multiple checkbox choices, scales, ratings and question grids.',
      'Search retained execution records by Control ID and department; completed PDFs display the non-controlled-version notice.',
      'Previously assigned records keep their independent Quality review workflow.',
    ],
    image: '2026-10-08-eqms-flexible-authoring.png',
  },
  {
    id: '2026-10-07-installation-job-quote-picker',
    date: '2026-10-07',
    title: 'Select Quotes When Creating Installation Jobs',
    description:
      'The quote picker supports scrolling, searching and selecting quotes inside the job form. The list fits smaller screens so quote details can be filled in before creating the job.',
    category: 'fix',
  },
  {
    id: '2026-10-06-qms-approved-status-permission',
    date: '2026-10-06',
    title: 'Separate Permission for Approved Ticket Status',
    description:
      'QMS status changes keep their existing permission. Setting a ticket to Approved now also requires the separate approval status permission, available on employee profiles and in QMS settings.',
    category: 'improvement',
  },
  {
    id: '2026-10-05-remove-global-atlas-widget',
    date: '2026-10-05',
    title: 'Remove the Global Atlas Widget',
    description:
      'The global Atlas chat widget and its profile preference have been removed. Account, tender and accounts receivable assistants remain available on their relevant pages.',
    category: 'improvement',
  },
  {
    id: '2026-10-01-eqms-controlled-forms',
    date: '2026-10-01',
    title: 'Controlled Forms and Procedure Executions',
    description:
      'ATEK employees can complete bilingual procedure forms together, retain an audit trail and obtain independent Quality approval. Cindy and Ilyas manage controlled templates and procedure revisions; completed records export to PDF with a clear non-controlled-version notice.',
    category: 'feature',
    details: [
      'Procedure assignments include exact released form versions, collaborators, a due date and equipment references.',
      'Text, numbers, dates, choices, attachments, table rows, calculations and conditional questions.',
      'Submitted records are frozen; signed amendments preserve the original completed record.',
    ],
    image: '2026-10-01-eqms-controlled-forms.png',
  },
  {
    id: '2026-09-29-eqms-glossary-template',
    date: '2026-09-29',
    title: 'Start a QMS Glossary from the ATEK Template',
    description:
      'Create a controlled Glossary with the ATEK terms and definitions table, eight guided sections and a changes log. Edit its content and printed details, then review the Letter PDF before signing.',
    category: 'improvement',
    image: '2026-09-29-eqms-glossary-template.png',
  },
  {
    id: '2026-09-28-eqms-procedure-template',
    date: '2026-09-28',
    title: 'Start Procedures from the ATEK Template',
    image: '2026-09-28-eqms-procedure-template.png',
    description:
      'New Procedure documents start with the ATEK bilingual template, including document headers, ten sections per language, revision logs and blank appendix tables. Edit the printed date and revision, then preview the complete PDF before signing.',
    category: 'improvement',
  },
  {
    id: '2026-09-24-eqms-native-document-authoring',
    date: '2026-09-24',
    title: 'Write Controlled Documents in eQMS',
    description:
      'Create a document from a blank page or SOP outline, with formatting, tables, images and saved working drafts. Preview a fixed PDF before signing, then review, release and revise it while preserving the previous effective version and its evidence.',
    category: 'feature',
    image: '2026-09-24-eqms-native-document-authoring.png',
  },
  {
    id: '2026-09-23-eqms-no-training-release',
    date: '2026-09-23',
    title: 'Release Documents Without Training',
    description:
      'Cindy can select "No training required" when making a controlled document effective. The signed choice prevents training assignments for that document and appears in its record; leaving the option unchecked keeps the usual training workflow.',
    category: 'improvement',
  },
  {
    id: '2026-09-23-eqms-quality-director-approval',
    date: '2026-09-23',
    title: 'Quality Director Document Approval Exception',
    description:
      'Cindy can approve her own controlled documents in eQMS. Approval still requires a quality role, password confirmation, a reason, and an electronic signature recorded in the audit trail; other owners still need an independent reviewer.',
    category: 'improvement',
  },
  {
    id: '2026-09-23-qms-code-fix-rejection-details',
    date: '2026-09-23',
    title: 'Clear Reasons When an AI Code Fix Needs Review',
    description:
      'QMS tickets explain rejected code fixes and retain a source-supported explanation when the AI proposes no change, including when the fix already appears in the code. Investigation findings remain available, and source review stays separate from verification of the running application.',
    category: 'fix',
  },
  {
    id: '2026-09-21-qms-code-fix-proposals',
    date: '2026-09-21',
    title: 'Code Fix Proposals Linked to QMS Tickets',
    description:
      'Start or rerun AI investigations directly from existing QMS tickets. When a software defect is identified, the ticket links to a proposed draft fix PR in the ATEK monorepo or explains why further review is needed.',
    category: 'feature',
  },
  {
    id: '2026-09-15-eqms-owner-release',
    date: '2026-09-15',
    title: 'Make Your Approved Documents Effective',
    description:
      'Document owners can now make their documents effective after another authorized reviewer approves them. Release still requires an electronic signature and is recorded in the audit trail.',
    category: 'fix',
  },
  {
    id: '2026-09-10-eqms-signature-feedback',
    date: '2026-09-10',
    title: 'Clear Feedback When an eQMS Signature Fails',
    description:
      'Signing errors now stay visible in the dialog with guidance for credentials, permissions, and connection problems. Invalid control codes are caught before signing, and your form details stay available after a rejected attempt.',
    category: 'fix',
  },
  {
    id: '2026-09-08-private-document-access',
    date: '2026-09-08',
    title: 'Documents Stay Private to Their Uploader',
    description:
      'Documents shows only files you uploaded, including within your company. When a document is no longer accessible, its cached preview and download links are cleared from the page.',
    category: 'fix',
  },
  {
    id: '2026-09-07-searchable-office-documents',
    date: '2026-09-07',
    title: 'Search Inside Office Documents and CSV Files',
    description:
      'DMS extracts content from Word (.docx), Excel (.xlsx), PowerPoint (.pptx), and CSV uploads for document search and classification. Files process in the background, with extracted content available in the document preview.',
    category: 'improvement',
    details: ['Controlled eQMS documents continue to follow their existing approval and access rules.'],
  },
  {
    id: '2026-09-04-installation-job-review-accuracy',
    date: '2026-09-04',
    title: 'More Accurate Installation Job Reviews',
    description:
      'Installation review now keeps the job-associated quote available when creating an IQ, and completion emails report progress across both job and sensor checklists.',
    category: 'fix',
    details: [
      'The quote saved with the technician IQ draft remains visible and selectable even when it is outside the current account quote page',
      'Job-submission emails include completed checklist items attached to individual sensors instead of incorrectly showing 0/0',
    ],
  },
  {
    id: '2026-09-03-sensor-intervention-reports',
    date: '2026-09-03',
    title: 'Auditable Sensor Intervention Reports',
    description:
      'Technicians can document sensor work with structured findings and photo evidence, while account teams can review the submitted history from the account dashboard.',
    category: 'feature',
    details: [
      'Assigned technicians can save drafts and submit an immutable report from the sensor workspace',
      'Submitted reports preserve sensor context, technician attribution, work performed, outcome, follow-up, photos, and timestamped corrections',
      'The account dashboard includes a read-only Interventions tab with filtering, detail review, and print-to-PDF support',
    ],
  },
  {
    id: '2026-09-03-eqms-inline-dms-upload',
    date: '2026-09-03',
    title: 'Upload Source Documents from eQMS',
    description:
      'Quality managers can now upload a missing source document to DMS without leaving the controlled-document workflow.',
    category: 'improvement',
    details: [
      'The source-document chooser now offers an inline DMS upload when the required file is not listed',
      'A successful upload is selected automatically while the electronic-signature step remains an explicit action',
      'Existing DMS duplicate detection is preserved to avoid unnecessary copies',
    ],
    image: '2026-09-03-eqms-inline-dms-upload.png',
  },
  {
    id: '2026-08-28-calibration-planning-upcoming',
    date: '2026-08-28',
    title: 'Calibration Planning: Upcoming Tab',
    description:
      'The Calibration Planning page now has Overview, Expired, and Upcoming tabs. Upcoming lists every probe due in the next three months, badged by who calibrates it, with a probe-type by location grid and a 13-week lab queue.',
    category: 'feature',
    details: [
      'New Upcoming tab covers every account (no contract filter), badged ATEK / Client / To verify',
      'A probe-type by location grid shows what calibrates on-site, ships to the lab, or ships a reference standard to the client — click a cell to filter the list',
      'A 13-week lab queue highlights weeks over capacity, counting only ATEK bench work',
      'Split-location incubators (temperature/humidity plus CO₂ or luminosity) show their gas leg on-site and their temperature/humidity leg at the lab',
      'The Expired drill-down moved from a slide-over into its own tab',
    ],
  },
  {
    id: '2026-08-24-technician-th-installation-reliability',
    date: '2026-08-24',
    title: 'Reliable Multi-Channel TH Installations',
    description:
      'Technician installations now treat a temperature/humidity transmitter as one physical device for checklist work while preserving a separate IQ for every sensor channel.',
    category: 'fix',
    details: [
      'Temperature and humidity channels share one physical-device checklist and its photo evidence',
      'Stream linking remains blocked until a packet received within the last 20 minutes proves live communication',
      'Creating one IQ updates only its sensor draft, so drafts for sibling channels are no longer overwritten',
    ],
  },
  {
    id: '2026-08-07-ivr-workspace-retired',
    date: '2026-08-07',
    title: 'IVR Workspace Removed from React Admin',
    description:
      'The React admin no longer exposes IVR pages or browser telephony controls, reducing unused navigation and background client behavior.',
    category: 'improvement',
    details: [
      'Removed the IVR dashboard, call logs, users, groups, queues, contacts, extensions, settings, and Softphone pages',
      'Removed the IVR sidebar group and retired all /ivr routes in the React admin',
      'Backend telephony services and alert delivery are unchanged',
    ],
  },
  {
    id: '2026-08-07-technician-sensor-health-and-scan',
    date: '2026-08-07',
    title: 'Faster Sensor Triage for Technicians',
    description:
      'Technicians can now spot sensors that need attention, scan a device label, and share a concise diagnostic summary from the account sensor workspace.',
    category: 'improvement',
    details: [
      'Reporting freshness is shown separately from the sensor lifecycle state, with clear delayed, late, missing, and never-reported states',
      'Needs attention combines alert state, maintenance, missing data, and low battery into one account- and site-aware filter',
      'The detail health rail summarizes data freshness, power, signal, and calibration at a glance',
      'Camera scanning fills the MAC search field locally, while URL-backed filters make the current view easy to restore',
      'Diagnostics can be shared with supported tablet apps or copied without changing sensor configuration',
    ],
    image: '2026-08-07-technician-sensor-health.png',
  },
  {
    id: '2026-08-06-per-account-voice-alert-provider',
    date: '2026-08-06',
    title: 'Per-Account Voice Alert Provider',
    description:
      'Operations can now choose Twilio or Telnyx for each account\'s spoken call and numeric pager alerts, while Twilio remains the default.',
    category: 'feature',
    details: [
      'The provider is selected from the account Settings tab',
      'Each queued alert keeps its selected provider across retries',
      'Telnyx is used only when explicitly selected, with no automatic provider fallback',
    ],
  },
  {
    id: '2026-08-05-technician-account-sensor-lookup',
    date: '2026-08-05',
    title: 'Assigned-Account Sensor Lookup for Technicians',
    description:
      'Technicians can now choose an assigned customer account, find sensors by name or MAC ID, filter by site and status, and inspect field details from a tablet-friendly workspace.',
    category: 'feature',
    details: [
      'Account administrators can assign an existing enabled employee or contractor from the account Users tab',
      'Account results are limited to organisations explicitly assigned to the signed-in employee',
      'Server-side name, MAC ID, site, and status filters stay accurate across large sensor inventories',
      'Sensor details combine the uploaded device photo, live state, placement, hardware identifiers, and calibration information',
      'Landscape tablets use a list-and-detail workspace while portrait tablets use a focused drill-in flow',
    ],
    image: '2026-08-05-technician-sensor-browser.jpg',
  },
  {
    id: '2026-08-05-quote-shipping-labels',
    date: '2026-08-05',
    title: 'Create Shipping Labels from a Quote',
    description:
      'Operations can now generate a Planète Courrier shipping label directly from a quote or the Shipping page, with carrier rates shopped live and the label ready to print in one flow.',
    category: 'feature',
    details: [
      'A new "Create shipping label" button on quotes and a "New shipping label" button on the Shipping page open a guided details, rates, and confirmation flow',
      'Destination address, recipient contact, references, and package details prefill automatically from the linked quote',
      'Carriers are rate-shopped live against Planète Courrier before purchase, and the winning label opens for printing immediately after the label is bought',
      'Reusable package size presets can be created, edited, and applied to any shipment',
      'The flow is restricted to full admins, directors, and operations managers',
    ],
  },
  {
    id: '2026-08-03-rejected-installation-access-code',
    date: '2026-08-03',
    title: 'Automatic Technician Access for Rejected Installation Jobs',
    description:
      'When Operations rejects an installation job by email, the technician now receives the rejection reason, portal link, and a newly generated access code in one message.',
    category: 'fix',
    details: [
      'Previous active access codes are revoked before a fresh seven-day code is generated',
      'The new code is included directly in the rejection email so no separate manual send is required',
      'If email delivery fails, the job remains rejected and Operations can immediately copy the one-time code from the dialog',
    ],
  },
  {
    id: '2026-07-29-account-user-creation-access-control',
    date: '2026-07-29',
    title: 'Account User Creation Access Control',
    description:
      'Administrators can now grant selected employees permission to create users from an account, without granting broader full-admin access.',
    category: 'improvement',
    details: [
      'Added a Create users switch to each employee\'s Access settings',
      'Account managers with this permission can use New User from an account\'s Users tab',
      'User deletion and permission administration remain restricted to full admins',
    ],
  },
  {
    id: '2026-07-24-eqms-validation-inspection-readiness',
    date: '2026-07-24',
    title: 'eQMS Validation and Inspection Readiness',
    description:
      'Quality leaders can now trace every eQMS requirement to current controls or explicit validation targets, sign objective validation results, freeze management reviews, and export audit-ready evidence packages.',
    category: 'feature',
    details: [
      'The traceability register maps all 23 named eQMS requirements to their source, acceptance criteria, current control or explicit validation target, validation state, and signed evidence',
      'Pass and Fail results require credential reauthentication and create immutable UTC signature manifestations without hiding unresolved gaps',
      'Management reviews freeze current document, CAPA, change-control, training, and validation metrics together with decisions and action items',
      'Inspection packages preserve the exact source manifest, record counts, cutoff, generator, and SHA-256 digest so later business-record changes cannot rewrite prior evidence',
      'Quality teams can filter, sort, configure columns, copy, and export the complete requirements register to CSV',
    ],
    image: '2026-07-24-eqms-validation-readiness.png',
  },
  {
    id: '2026-07-23-eqms-training-management',
    date: '2026-07-23',
    title: 'eQMS Training Management and Qualification',
    description:
      'Effective controlled procedures now create governed employee training assignments with attributable reading, assessment, qualification, and compliance evidence.',
    category: 'feature',
    details: [
      'Training matrices connect controlled-document groups to employees, due dates, assessment thresholds, maximum attempts, and supervisor escalation',
      'Releasing a controlled document as Effective creates its version-specific assignments in the same database transaction',
      'Employees follow a guided Read, Assess, and Sign evidence path using the current Effective controlled copy',
      'Assessment answers are scored on the server, and a final failed attempt locks qualification and creates a permanent supervisor alert',
      'Completion requires username and password re-entry and records an immutable electronic signature with the meaning qualification and a UTC manifestation',
      'Quality teams can review the red, yellow, and green compliance register, search and sort assignments, configure table columns, and export evidence to CSV',
    ],
  },
  {
    id: '2026-07-23-eqms-quality-event-workflows',
    date: '2026-07-23',
    title: 'eQMS CAPA and Change Control Workflows',
    description:
      'Quality teams can now manage corrective actions and controlled changes through governed, evidence-backed workflows with credentialed electronic signatures.',
    category: 'feature',
    details: [
      'CAPAs capture a formal root-cause method plus separate immediate, corrective, and preventive actions with accountable owners, due dates, evidence, and verification',
      'Completing a CAPA schedules its effectiveness review for 60 days later, and final validation requires signed QA metrics and verified actions',
      'Change requests record the current and proposed state, justification, impact, a 3-by-3 risk assessment, implementation tasks, outcomes, and review notes',
      'Major and critical changes require every listed Change Control Board member to sign an approval before implementation can proceed',
      'Protected status moves, approvals, rejections, and effectiveness decisions require password re-entry and create permanent signature and audit records',
      'Changes that affect an SOP cannot close until the linked controlled document is Effective',
    ],
  },
  {
    id: '2026-07-23-eqms-controlled-documents',
    date: '2026-07-23',
    title: 'eQMS Controlled Document Command Center',
    description:
      'Quality leaders now have one controlled workspace for document ownership, review, approval, release, electronic signatures, and permanent audit evidence.',
    category: 'feature',
    details: [
      'Documents move through a governed Draft, In Review, Approved, Effective, and Obsolete lifecycle with controlled version numbers',
      'Every lifecycle action requires the current user to re-enter their username and password, select the mandated signature meaning, and record a reason',
      'Document owners cannot approve or release their own records, preserving segregation of duties',
      'Signature manifestations and read/change audit events are visible in each controlled record and protected from editing or deletion',
      'The controlled register includes server pagination, filters, sorting, configurable columns, clipboard copy, and page or full CSV export',
    ],
  },
  {
    id: '2026-07-22-kanban-backward-move-reason',
    date: '2026-07-22',
    title: 'Operations Kanban: Reason Required When Moving a Card Back',
    description:
      'Cards on the Operations Kanban can be dragged backward to correct a mistake, but doing so now requires a written reason that is kept in a permanent, tamper-proof audit trail.',
    category: 'improvement',
    details: [
      'Dragging a card to an earlier stage opens a dialog asking why — the move is blocked until a reason is entered',
      'The reason, the stages involved, and the name of the person who moved the card are recorded together',
      'The operations audit log is now append-only at the database level: these entries can never be edited or deleted',
    ],
  },
  {
    id: '2026-07-22-installation-iq-creation-restored',
    date: '2026-07-22',
    title: 'Installation IQ Creation Restored',
    description:
      'Operations can once again approve a technician draft and create the completed Installation IQ directly from the installation job.',
    category: 'fix',
    details: [
      'Approve & Create IQ now reaches the existing qualification workflow through the authenticated React API',
      'The operator identity and customer organisation are preserved when the IQ report is created and audited',
      'The confirmation message and View action now retain the completed IQ report number and destination',
      'New Installation IQ certificates are mirrored into PostgreSQL so the existing qualification listing can find them by report ID or number',
      'R&D test certificates and legacy certificate payloads are accepted by the PostgreSQL qualification listing',
      'View actions and the Gateway IQ shortcut now open the V1 legacy application',
    ],
  },
  {
    id: '2026-07-22-installation-iq-access-control',
    date: '2026-07-22',
    title: 'Installation IQ Access Control',
    description:
      'The installation job IQ review tab is now limited to teammates who have been granted IQ access in HRIS.',
    category: 'fix',
    details: [
      'Added a Review IQ switch to each employee\'s Access settings',
      'Full admins retain access; directors and other teammates need the explicit permission',
    ],
  },
  {
    id: '2026-07-22-technician-iq-draft-review-restored',
    date: '2026-07-22',
    title: 'Technician IQ Draft Review Restored',
    description:
      'Operations can now review a technician\'s IQ draft as soon as it is submitted, with cleaner installation evidence and reliable stream linking during setup.',
    category: 'fix',
    details: [
      'Submitted IQ drafts appear while the installation job is pending review, without waiting for job completion',
      'Operations receives an immediate email when each sensor IQ draft is ready for review',
      'Application and probe type use platform choices, and IQ evidence now includes only the sensor installation photos',
      'Technicians can find transmitter streams by DevEUI before the sensor is assigned or the stream is renamed',
      'IQ creation uses the installation job account when a newly installed sensor is still waiting for PostgreSQL account allocation',
      'Technician photos transfer through the authenticated API, so IQ creation no longer depends on direct browser access to file storage',
      'Numeric transmitter hardware and display configuration values are normalized before IQ creation',
    ],
  },
  {
    id: '2026-07-20-stream-visibility-restored',
    date: '2026-07-20',
    title: 'Legacy Stream Visibility Restored',
    description:
      'The Streams page now includes eligible historical streams that were previously visible only in the legacy platform, restoring V1/V2 search parity for operations teams.',
    category: 'fix',
    details: [
      'Previously missing historical streams are now searchable in the modern Streams page',
      'New streams created by legacy ingestion are added to V2 automatically',
      'Existing PostgreSQL stream records remain authoritative and are never overwritten by this synchronization',
    ],
  },
  {
    id: '2026-07-15-data-recovery-reliability',
    date: '2026-07-15',
    title: 'More Reliable Chart Data Recovery',
    description:
      'Chart data recovery now targets the exact missing readings and reports completion more accurately across Milesight, Dragino LHT65, and XBee sensors.',
    category: 'fix',
    details: [
      'Requests preserve the sensor cadence and exclude known readings at either side of a gap',
      'Duplicate requests and scheduler retries are handled safely without sending the same recovery downlink twice',
      'Partial recoveries, device no-data responses, and delivery failures now produce accurate audited outcomes and notifications',
    ],
    legacyFrontend: true,
  },
  {
    id: '2026-07-15-dragino-lht65-chart-data-recovery',
    date: '2026-07-15',
    title: 'Dragino LHT65 Chart Data Recovery',
    description:
      'ATEK full admins can now request missing LHT65 and LHT65N readings directly from the chart using the device\'s retained datalog history.',
    category: 'feature',
    details: [
      'The existing Start data recovery action now appears for supported LHT65 temperature, humidity, and external-probe channels',
      'Requests use an isolated Dragino queue and recovery scheduler so Milesight and Dragino downlink commands cannot be mixed',
      'Recovered records retain their original device timestamps and flow through the existing audit, cooldown, completion, and email workflow',
    ],
    legacyFrontend: true,
  },
  {
    id: '2026-07-15-xbee-chart-data-recovery',
    date: '2026-07-15',
    title: 'XBee Chart Data Recovery',
    description:
      'ATEK full admins can now restore missing XBee chart readings from retained gateway packets, extending the data-recovery workflow beyond Milesight devices.',
    category: 'feature',
    details: [
      'The chart recognizes legacy XBee streams and offers the existing Start data recovery action',
      'Recovery restores only missing MongoDB buckets and republishes calibrated readings to ClickHouse so the repaired data appears on charts',
      'Requests remain bounded and audited, and clearly report when no matching packets remain in the archive',
    ],
    legacyFrontend: true,
  },
  {
    id: '2026-07-13-alert-schedule-safety',
    date: '2026-07-13',
    title: 'Alert Schedule Safety Validation',
    description:
      'Notification conditions can no longer be saved with blank, partial, or malformed schedules. Existing conditions with damaged schedule data now remain active continuously instead of being silently disabled.',
    category: 'fix',
    details: [
      'Schedule fields now show an inline error and provide a one-click All day (00:00 - 00:00) option',
      'The API rejects invalid schedules before saving and identifies the affected rule field',
      'The alert scheduler logs malformed legacy schedules and evaluates them as active 24/7 until they are corrected',
    ],
    legacyFrontend: true,
  },
  {
    id: '2026-07-10-sensors-pages-staff-visibility',
    date: '2026-07-10',
    title: 'Sensors Pages Visible to All Staff Roles',
    description:
      'The Sensors and Inactive Sensors pages under Operations now load data for every staff role (sales, operations, quality, director). Previously only full admins received data — everyone else saw an empty table with no explanation.',
    category: 'fix',
    details: [
      'The sensors list, inactive list, and fleet summaries were gated to full admins on the API side while the pages themselves were open to all staff roles',
      'Permission denials now surface as a clear error instead of silently rendering an empty table',
    ],
  },
  {
    id: '2026-07-06-quote-priority-kanban-badge',
    date: '2026-07-06',
    title: 'Quote Priority on the Operations Kanban',
    description:
      'Quotes now carry an optional priority — Urgent, Normal, or Low — shown as a colored badge on the Operations Kanban cards so the operations team can see at a glance which quotes to work first.',
    category: 'feature',
    details: [
      'Priority select in the quote form (pre-filled "Normal", never mandatory) — the Account Manager can set it at creation',
      'Editable anytime from the Kanban card detail panel — the operations coordinator can (re)prioritize without opening the quote',
      'Colors: Urgent = red, Normal = blue, Low = gray; quotes created before this feature show no badge until a priority is set',
    ],
  },
  {
    id: '2026-06-30-duplicate-quote',
    date: '2026-06-30',
    title: 'Duplicate a Quote',
    description:
      'Quotes now have a Duplicate action that opens the New Quote form pre-filled from an existing quote — same customer, line items, managers, and terms — so you can spin up a variant in a few clicks instead of re-entering everything.',
    category: 'feature',
    details: [
      'A Duplicate button sits beside Edit / Print / Delete on each row of the Quotes list (gated by the quote-create permission)',
      'Opens the form in create mode pre-seeded from the source quote: customer, sales & contractual managers, contract, line items, project, description, deal type, currency, language, payment terms, tax category, and addresses',
      'Identity and lifecycle fields reset — a fresh quote number is assigned on save, status returns to Preparation, and PO number, dates, tracking numbers, HubSpot/Drive links, and internal notes are cleared',
      'Saving creates a brand-new PostgreSQL-native quote; the original is left untouched',
    ],
  },
  {
    id: '2026-06-26-workflow-scheduler',
    date: '2026-06-26',
    title: 'Workflow Scheduler',
    description:
      'The /tools/scheduled-jobs page is now a Workflow Scheduler that manages ATEK\'s Mastra workflow scheduler, allowing users to enable/disable workflows, edit cron schedules, and trigger runs on demand.',
    category: 'improvement',
    details: [
      'Enable or disable any registered Mastra workflow from a single interface',
      'Edit cron expressions to adjust workflow schedules',
      'Trigger immediate workflow runs with the run-now button',
    ],
  },
  {
    id: '2026-06-21-calibration-planning-cockpit',
    date: '2026-06-21',
    title: 'Calibration Planning Cockpit',
    description:
      'New operations page at /operations/calibration/planning that shows a 24-month lab load curve, onsite trip scheduling, ship/lab weekly queues, and a re-pitch panel for uncovered accounts — all from a live KPI strip covering in-plan, covered, partial, verify, expired, and capacity metrics.',
    category: 'feature',
    details: [
      'KPI strip: in-plan / covered / partial / verify / expired / re-pitch counts plus lab demand-vs-capacity ratio',
      '24-month stacked load curve broken down by method (Ship / DS18B20 / Onsite)',
      'Onsite trips-to-book table with region, probe count, ≤3-day trip groupings, and fly-out flag',
      'Ship and lab weekly queue panels',
      'Re-pitch panel listing uncovered accounts for the sales team',
      'Gated by calibrationrecords.read permission; data from GET /v4/calibration-certificates/planning',
    ],
  },
  {
    id: '2026-06-18-call-logs-crm-links-recording-player',
    date: '2026-06-18',
    title: 'Call Logs: CRM Links, Inline Recording Player, and AI Outcome Badge',
    description:
      'The Call Logs page now links each call to its CRM and QMS entities, plays recordings directly in the list, and shows the AI-detected call outcome as a badge.',
    category: 'feature',
    details: [
      'Each call row shows an "Attached to" cell with clickable links to the caller\'s contact, company, account, opportunities, and tickets, navigating to the V4 pages',
      'A play button and clickable duration field launch inline audio playback of the call recording without leaving the page; only one recording plays at a time',
      'Recordings stream through the authenticated /v4/ivr/calls/:callSid/recording endpoint so playback works without exposing raw Twilio URLs',
      'The call detail panel recording playback was updated to use the same authenticated stream (previously pointed at an unplayable raw Twilio URL)',
      'The AI-detected call outcome is now shown as a badge in the status cell alongside the call status',
    ],
  },
  {
    id: '2026-06-18-task-account-activity',
    date: '2026-06-18',
    title: 'Task Changes in Account Activity',
    description:
      'Accounts now show activity entries when related quote tasks are created or moved between task statuses.',
    category: 'improvement',
    details: [
      'Task activity is linked through the task quote to the related account',
      'The account Activity tab now includes a Tasks filter for task-created and task-status updates',
    ],
  },
  {
    id: '2026-06-17-task-quote-linking',
    date: '2026-06-17',
    title: 'Tasks Link Directly to Quotes',
    description:
      'The task form now links tasks to quotes directly instead of routing the relationship through operations projects.',
    category: 'fix',
    details: [
      'The task create/edit dialog now shows Quote instead of Project',
      'Task filters, cards, list rows, and detail panels now resolve quote numbers from the direct quote link',
    ],
  },
  {
    id: '2026-06-15-kanban-invoice-workflow',
    date: '2026-06-15',
    title: 'Create Invoices from the Operations Kanban',
    description:
      'Account managers can start an invoice directly from quotes in the Invoicing column, with quote data and uninvoiced sensors preselected.',
    category: 'feature',
    details: [
      'Open the invoice workflow from the quote detail panel',
      'Right-click an Invoicing card to access Create Invoice',
      'Reuse the existing quote invoice form without QuickBooks integration',
    ],
  },
  {
    id: '2026-06-10-subscription-contractual-manager-parity',
    date: '2026-06-10',
    title: 'Subscription Contractual Manager List Restored',
    description:
      'Subscription forms now show the same customer contacts tagged as Contractual Managers in the legacy platform, so missing names can be assigned in V2.',
    category: 'fix',
    details: [
      'Manager choices are scoped to the subscription account and its legacy organisation',
      'Existing historical assignments remain visible even when they are no longer tagged',
      'The contractual manager field supports adding multiple tagged contacts',
    ],
  },
  {
    id: '2026-06-08-invoice-ai-memory-tab',
    date: '2026-06-08',
    title: 'Invoice AI Memory tab',
    description:
      'A new "Memory" tab on the invoice detail panel shows what the AI accounting engine has recorded for that invoice — payment status, bank and Stripe matches, and reconciliation notes — pulled live from the per-invoice AI thread.',
    category: 'feature',
    details: [
      'Memory tab appears on the invoice comparison panel opened from the Accounts Receivable page',
      'Renders the AI reconciliation thread\'s working memory as formatted text',
      'Content is fetched lazily: no network request is made until the tab is opened',
      'Displays a friendly message when an invoice has no AI memory recorded yet',
      'Read-only view — the tab surfaces what the engine has learned without exposing edit controls',
    ],
  },
  {
    id: '2026-06-08-quote-ticket-linking-restored',
    date: '2026-06-08',
    title: 'Quote Ticket Linking Restored',
    description:
      'Quote forms now load the complete customer support-ticket history, so valid legacy tickets can be linked to quotes again.',
    category: 'fix',
    details: [
      'Ticket options now use the authoritative legacy support-ticket source through a quote-authorized API',
      'Accounts with large ticket histories now load additional pages instead of hiding older records',
    ],
  },
  {
    id: '2026-06-04-qms-trend-pattern-actions',
    date: '2026-06-04',
    title: 'More Useful QMS Trend Patterns',
    description:
      'The QMS Trends page now makes pattern results easier to trust and act on from the ticket list.',
    category: 'improvement',
    details: [
      'Trend analysis now detects SLA breach clusters, overdue CAPA actions, and approval bottlenecks',
      'Zero-baseline department spikes now show as new spikes instead of misleading 999% increases',
      'Stale trend analyses are now called out when the daily computation has not refreshed',
      'Repeat issue and location hotspot cards can now open the related tickets directly',
    ],
  },
  {
    id: '2026-06-03-quotes-list-reliability-fixes',
    date: '2026-06-03',
    title: 'Quotes List Reliability Fixes',
    description:
      'The Quotes page now updates list totals, sorting, and deleted rows more reliably while users work.',
    category: 'fix',
    details: [
      'Quote status totals now match the active table filters',
      'Client sorting now orders quotes by the account name shown in the table',
      'Deleted quotes now disappear from the list immediately after deletion succeeds',
    ],
  },
  {
    id: '2026-05-29-account-allocation-ui',
    date: '2026-05-29',
    title: 'Accounts: Organisation & Usergroup Allocation, Sensor Pin',
    description:
      'Account Settings now exposes a dedicated Allocation card for managing which organisations are linked to an account and which usergroups belong to it, plus a new Attached column in the Sensors tab for pinning sensors.',
    category: 'feature',
    details: [
      'Allocation card on Settings tab shows one column per linked organisation, each listing its usergroups as checkboxes',
      'Add or remove linked organisations from the card header without leaving the Settings tab',
      'Checking a free usergroup allocates it to the account; unchecking a usergroup that belongs to this account releases it',
      'Checking a usergroup currently owned by another account opens a move-confirmation AlertDialog showing the current owner before proceeding',
      'New "Attached" column in Sensors tab: checkbox bound to accountIdOverridden, Pin icon indicator when pinned',
      'Pin / unpin actions call the new backend endpoints and invalidate the sensors query',
      'Legacy Organisation Config card demoted to the bottom of the Settings tab',
    ],
  },
  {
    id: '2026-05-29-aging-filtered-totals-export',
    date: '2026-05-29',
    title: 'AR / AP Aging: Filtered Totals, CSV Export, and Accessibility',
    description:
      'Aging summary cards, the "Total outstanding" headline, and the totals row now reflect the currently-filtered rows instead of the full QuickBooks grand total. Both AR and AP pages gain a CSV export button and improved loading, error, and empty states.',
    category: 'improvement',
    details: [
      'Summary cards (Current / 1–30 / 31–60 / 61–90 / 91+ / Total) and the totals row recompute live as name, amount, or Hide-$0 filters are applied',
      'New "Export CSV" button exports the filtered aging rows (Name + each bucket + Total); filename includes the as-of date (ar-… / ap-…)',
      'Skeleton table and summary tiles display while data is loading instead of a bare spinner',
      'Load failures show an error panel with a "Try again" button',
      'When filters produce no results, a "No matches" empty state appears with a "Clear filters" button (includes the Hide-$0 toggle)',
      'Customer rows and invoice rows are keyboard-operable: Tab to focus, Enter or Space to expand a customer or open an invoice',
      'Per-bucket "Min $" filter inputs now have proper accessible labels',
      'Wide table scrolls horizontally on narrow screens without layout overflow',
      'Aging status colors migrated to semantic theme tokens for correct dark-mode rendering',
    ],
  },
  {
    id: '2026-05-29-quote-owned-operations-kanban',
    date: '2026-05-29',
    title: 'Quote-Owned Operations Kanban',
    description:
      'The Operations Kanban now uses quotes as the primary cards, with fulfillment stages tracked separately from commercial quote status.',
    category: 'improvement',
    details: [
      'Approved and active quotes can appear directly in the New Purchase Order and fulfillment columns',
      'Quote stage movement no longer depends on an operations project being the board card',
      'Supporting projects and tasks now keep a quote link for execution details',
    ],
  },
  {
    id: '2026-05-28-balance-sheet-usability',
    date: '2026-05-28',
    title: 'Balance Sheet Table Layout and Export',
    description:
      'The Balance Sheet page now displays all months and the Change column without clipping, and both Statement and Comparative tabs support CSV export.',
    category: 'improvement',
    details: [
      'Comparative tab: rightmost month columns and Change column are no longer clipped; the table expands to natural width with sticky Change column on the right and sticky Line Item column on the left',
      'Both tabs: new "Export CSV" button — Statement tab exports flat rows (Name/Type/Depth/Value); Comparative exports month-by-month matrix (Line item + 12 months + Change)',
      'Statement tab: initial load shows a skeleton matching the final layout instead of a bare spinner; load failures show an error panel with "Try again" button',
      'Comparative tab: shows "Loading periods…" indicator before the first month arrives instead of a blank table',
      'Section expand/collapse is now keyboard-accessible with Tab and Enter/Space, with screen-reader labels',
      'Balance sheet status colors migrated to semantic theme tokens (correct in dark mode)',
    ],
  },
  {
    id: '2026-05-28-account-communications-timeline',
    date: '2026-05-28',
    title: 'Account Communications Timeline',
    description:
      'Account dashboards now include a Communications tab for reviewing email, SMS, call, and alert sendout history in one place.',
    category: 'feature',
    details: [
      'Filter account communications by channel, status, date range, and search text',
      'Review delivery failures, provider IDs, and alert evidence without leaving the account page',
      'Copy or export the current filtered page for account follow-up work',
    ],
  },
  {
    id: '2026-05-27-qms-quality-of-life-improvements',
    date: '2026-05-27',
    title: 'QMS Quality of Life Improvements',
    description:
      'QMS ticket editing and board movement are now easier to use, with larger rich-text click targets and more reliable Kanban drag behavior.',
    category: 'improvement',
    details: [
      'Rich-text ticket fields now use the full visible editor area as a text click target',
      'Kanban drag and drop now detects target columns more reliably when moving cards',
      'Moved tickets now update board counts and cached ticket data consistently',
    ],
  },
  {
    id: '2026-05-27-quotes-permission-gates',
    date: '2026-05-27',
    title: 'Quotes Permission Gates',
    description:
      'The Quotes page now has dedicated HRIS permissions for page access and individual quote actions.',
    category: 'improvement',
    details: [
      'Added a dedicated Quotes group to the employee Access tab',
      'Create, edit, delete, print, export, and quote link actions now follow assigned permissions',
      'Full admins and directors continue to have access to all Quotes actions',
    ],
  },
  {
    id: '2026-05-27-transmitter-permission-gates',
    date: '2026-05-27',
    title: 'Transmitter Permission Gates',
    description:
      'The Transmitters page can now be assigned through HRIS permissions, including separate controls for device actions.',
    category: 'improvement',
    details: [
      'Added Transmitter read and action permissions to the employee Access tab',
      'Sidebar visibility and direct route access now follow the Transmitters read permission',
      'Edit, reassign, image, OTA, diagnostics, reset SIM, and delete actions now follow assigned permissions',
    ],
  },
  {
    id: '2026-05-26-quotes-pipeline-kanban-dashboard',
    date: '2026-05-26',
    title: 'Quotes Pipeline Kanban Dashboard',
    description:
      'The Quotes Pipeline page now shows quotes in status-based kanban columns instead of a project-oriented table.',
    category: 'improvement',
    details: [
      'Quote cards show quote number, PO number, account, value, status, and key dates',
      'Clicking a quote card now opens the full quote editor with the same workflow actions as All Quotes',
      'Kanban search supports quote number, PO number, account, and status',
    ],
  },
  {
    id: '2026-05-26-installation-job-permissions',
    date: '2026-05-26',
    title: 'Installation Job Permissions',
    description:
      'Employee Access tabs now include Installation Job permissions for creating, editing, and deleting technician jobs.',
    category: 'improvement',
    details: [
      'Added Installation Jobs permission toggles to HRIS employee profiles',
      'Includes create, edit, and delete permission strings for technician installation jobs',
    ],
  },
  {
    id: '2026-05-25-qms-email-ticket-links',
    date: '2026-05-25',
    title: 'QMS Email Ticket Links',
    description:
      'QMS comment notification emails now open the correct ticket in the React app instead of sending users to a local server address.',
    category: 'fix',
  },
  {
    id: '2026-05-25-os-permission-gates',
    date: '2026-05-25',
    title: 'OS Permission Gates',
    description:
      'OS pages such as KPIs, Rocks, Headlines, Issues, To-Dos, Meetings, and V/TO can now be assigned through HRIS permissions.',
    category: 'improvement',
    details: [
      'Added OS read and action permissions to the employee Access tab',
      'Sidebar visibility and direct route access now follow the assigned OS permissions',
      'Gateway checks now enforce OS and QMS issue permissions before forwarding API actions',
    ],
  },
  {
    id: '2026-05-22-accounting-permission-gates',
    date: '2026-05-22',
    title: 'Accounting Permission Gates',
    description:
      'Accounting pages can now be assigned through HRIS permissions instead of relying on full-admin-only access.',
    category: 'improvement',
    details: [
      'Added permissions for P&L, invoices, reconciliations, receivable, payable, bills, and suppliers',
      'Accounting API actions are enforced through gateway checks and rest_server marker validation',
      'Supplier access now supports either WMS supplier permissions or Accounting supplier permissions',
    ],
  },
  {
    id: '2026-05-21-stream-editing-postgresql',
    date: '2026-05-21',
    title: 'Stream Editing on PostgreSQL',
    description:
      'Creating, editing, and deleting streams in the React Streams page now saves directly to PostgreSQL instead of waiting for MongoDB CDC.',
    category: 'improvement',
    details: [
      'Stream form dropdowns now load from PostgreSQL-backed sensor, transmitter, and account data',
      'Editable calibration and filtering fields are stored on the PostgreSQL streams table',
      'Sensor stream assignments are updated immediately when a stream is saved or deleted',
    ],
  },
  {
    id: '2026-05-21-hris-user-permission-management',
    date: '2026-05-21',
    title: 'HRIS User Permission Management',
    description:
      'Full administrators can now manage a user’s permission strings from the HRIS employee detail page, including calibration certificate access.',
    category: 'feature',
    details: [
      'Added an Access tab on employee profiles with grouped permission toggles',
      'Included calibration certificate permissions such as calibrationrecords.read',
      'Supports custom permission strings while preserving existing assignments',
    ],
  },
  {
    id: '2026-05-21-calibration-table-sort-fix',
    date: '2026-05-21',
    title: 'Calibration Table Sorting Fix',
    description:
      'Calibration certificate table sorting now works reliably when switching from Due Date to other columns such as Equipment and Stream/Probe.',
    category: 'fix',
  },
  {
    id: '2026-05-21-calibration-saved-views-pdf-tables',
    date: '2026-05-21',
    title: 'Calibration Saved Views and Selectable PDF Tables',
    description:
      'Calibration certificate filters can now be saved as reusable views, and PDF exports use cleaner grouped tables with selectable columns.',
    category: 'feature',
    details: [
      'Save and reload calibration certificate filters from the existing server-backed saved views menu',
      'Saved views include active table column filters alongside URL-synced filters',
      'Export selected certificates as a PDF using visible table columns or a custom saved column set',
      'PDF exports keep the site and location grouping but render certificates in compact tables for easier review',
    ],
  },
  {
    id: '2026-05-21-calibration-summary-card-filters',
    date: '2026-05-21',
    title: 'Calibration Summary Card Filters',
    description:
      'Calibration certificate summary cards are now clickable filters, so operators can jump directly to overdue, upcoming, or later certificate groups from the dashboard totals.',
    category: 'improvement',
    details: [
      'Click Total, Overdue, Due in 30 days, Due in 60 days, Due in 90 days, or After 90 days to update the table filters',
      'Summary card filters use the same server-side pagination and URL-synced filtering as the table',
      'The active summary card is highlighted and can be selected with the keyboard',
    ],
  },
  {
    id: '2026-05-14-calibration-record-save-fix',
    date: '2026-05-14',
    title: 'Calibration Record Save Fix',
    description:
      'Calibration certificate edits in the legacy portal now save reliably when older records contain blank or placeholder previous-certificate values.',
    category: 'fix',
    details: [
      'Saving calibrated-device details no longer fails because of hidden legacy certificate placeholders',
      'Selecting a stream now refreshes the calibrated-device details and clears stale previous-calibration data when needed',
      'Optional previous-certificate references are normalized before saving',
    ],
    legacyFrontend: true,
  },
  {
    id: '2026-05-01-am-dashboard-manager-totals',
    date: '2026-05-01',
    title: 'AM Dashboard Manager Totals',
    description:
      'AM Dashboard totals now assign each account to one effective account manager, preventing the same inactive sensors from appearing under multiple manager filters.',
    category: 'fix',
    details: [
      'Account-level manager assignment is used first',
      'Current active or pending subscription manager is used only when the account has no direct manager',
      'All Account Managers remains the de-duplicated total across visible accounts',
    ],
  },
  {
    id: '2026-04-30-softphone-rings-across-admin-pages',
    date: '2026-04-30',
    title: 'Softphone Rings Across Admin Pages',
    description:
      'The browser softphone now stays connected while you work across the React admin app, so incoming calls can ring outside the Softphone page.',
    category: 'feature',
    details: [
      'Added a softphone connection indicator in the top header',
      'Incoming calls now show a compact accept/decline bar on non-softphone pages',
      'The Softphone page continues to provide the full dial pad and active-call controls',
    ],
  },
  {
    id: '2026-04-30-transmitter-raw-events-packet-limit',
    date: '2026-04-30',
    title: 'Transmitter Raw Events Packet Limit',
    description:
      'The Transmitter Raw Events page now has a Last X Packets filter and caches repeated archive lookups to reduce ClickHouse load during investigations.',
    category: 'improvement',
    details: [
      'Choose the last 20, 100, or 500 packets directly from the filter panel',
      'Repeated list and detail lookups are cached in Redis, with shorter caching for recent windows and longer caching for historical searches',
    ],
  },
  {
    id: '2026-04-27-location-account-search',
    date: '2026-04-27',
    title: 'Location Account Search',
    description:
      'The Create Location form now has a searchable account picker, making it easier to find the right customer account when creating locations.',
    category: 'improvement',
    details: [
      'Search accounts directly from the Account field instead of scrolling through a fixed list',
      'Large account lists now load progressively as you scroll',
      'Selected account names stay visible even when they are not in the first loaded result page',
    ],
  },
  {
    id: '2026-04-27-inactive-sensor-status-fix',
    date: '2026-04-27',
    title: 'Inactive Sensor Status Fix',
    description:
      'Inactive sensor counts and filters now treat only status 5 as inactive, with unmanaged and inhibited sensors shown as separate statuses.',
    category: 'fix',
    details: [
      'Inactive Sensors now requests status 5 only',
      'Account sensor filters now separate unmanaged, inactive, and inhibited statuses',
      'Sensor count tooltips now describe the corrected inactive status definition',
    ],
  },
  {
    id: '2026-04-23-installation-job-iq-creation',
    date: '2026-04-23',
    title: 'Installation Job IQ Creation',
    description:
      'Completed installation jobs now include an IQ phase directly in the React job detail page, so operators can create the installed sensor IQ without returning to the legacy portal.',
    category: 'feature',
    details: [
      'Added a dedicated IQ tab for completed jobs with installed sensors',
      'Auto-fills the linked quote, live sensor and transmitter context, and technician checklist photos',
      'Creates the legacy installation qualification from the approved job context in one flow',
    ],
  },
  {
    id: '2026-04-21-transmitter-raw-events-debug-archive',
    date: '2026-04-21',
    title: 'Transmitter Raw Events Debug Archive',
    description:
      'A new Transmitter Raw Events page gives operations a dedicated place to inspect raw ingress data and malformed packet traffic without relying on the capped transmitter diagnostics history.',
    category: 'feature',
    details: [
      'Added a dedicated debug page for raw UDP, api_checker HTTP, and LoRaWAN ingress events',
      'Archive rows include status, source, device identifiers, packet metadata, and the original payload for investigation',
      'Hex payloads can be opened directly in the built-in Packet Interpreter tool',
      'Added a shortcut from the transmitter Diagnostics tab into the new raw archive page',
    ],
  },
  {
    id: '2026-04-14-am-dashboard-manager-scope-fix',
    date: '2026-04-14',
    title: 'AM Dashboard Manager Scope Fix',
    description:
      'Selecting an account manager on the AM Dashboard now includes accounts linked through subscriptions, quotes, and invoices instead of relying only on the direct account-level manager field.',
    category: 'fix',
    details: [
      'Summary cards and account results now reflect the broader manager ownership model already used elsewhere in the platform',
      'Managers with historical or partially backfilled account assignments now see the expected portfolio activity instead of empty top-line metrics',
    ],
  },
  {
    id: '2026-04-14-account-user-groups-bulk-selection-fix',
    date: '2026-04-14',
    title: 'Account User Groups Bulk Selection Fix',
    description:
      'The Account Settings User Groups picker now supports selecting all visible groups at once, and clearing the selection no longer makes the list disappear for the current organisation.',
    category: 'fix',
    details: [
      'Added a Select all action to the User Groups multi-select in Account Settings',
      'Fixed a regression where unassigning every group could leave the dropdown empty until the data was refreshed',
      'Unassigned groups now remain available in the picker for the same organisation',
    ],
  },
  {
    id: '2026-04-14-report-pdf-freshness-and-timezone-notices',
    date: '2026-04-14',
    title: 'Temperature Reports Now Show Timezone Notes',
    description:
      'Temperature report PDFs now explain the reference timezone and UTC-to-local timestamp display rules, including daylight saving time cases. Current-period reports are also regenerated more reliably instead of reusing stale cached output.',
    category: 'fix',
    details: [
      'Added explicit timezone notes to calendar, monthly, hourly, daily log, min/max, and sensor group temperature reports',
      'Timezone copy now distinguishes fixed-offset periods from daylight saving time transitions within the report range',
      'Current-period report generation now bypasses stale cache reuse so newer readings are reflected more consistently in regenerated PDFs',
      'Aligned report rolling-average math with the chart calculation to remove small report-versus-graph drift',
    ],
  },
  {
    id: '2026-04-14-user-creation-parity-fields',
    date: '2026-04-14',
    title: 'Expanded User Creation Fields',
    description:
      'The React Create User modal now covers the main legacy account setup fields, so new users can be configured without dropping back to the old portal.',
    category: 'improvement',
    details: [
      'Added username, enabled status, force password reset, session timeout, phone extension, and subaccount-without-email controls',
      'Added notification methods, account-profile tags, and account-profile description during user creation',
      'User group selection now uses the same badge-style picker as account settings, including a select-all action',
      'Keeps the existing MongoDB-first write flow and waits for the synced V4 read model before refreshing the UI',
    ],
  },
  {
    id: '2026-04-13-account-users-tab-management',
    date: '2026-04-13',
    title: 'Account-Level User Management',
    description:
      'The Users tab on each account now lets you create and edit account users without leaving the dashboard.',
    category: 'feature',
    details: [
      'New User button opens the shared create-user modal already scoped to the current account',
      'Edit actions now use the shared React user form instead of sending you to the legacy page',
      'User changes refresh from the synced V4 read model after the write completes',
    ],
  },
  {
    id: '2026-03-31-invoice-recurring-filter',
    date: '2026-03-31',
    title: 'Invoice Recurring Filter',
    description:
      'The Invoices tab in the Subscription Form Modal now includes a filter to distinguish between recurring and non-recurring invoices based on the linked quote type.',
    category: 'improvement',
    details: [
      'Toggle between All, Recurring, and Non-recurring invoices',
      'Filter is based on the quote\'s deal type (recurring, newbusiness, existingbusiness, rfp)',
      'Invoices without a linked quote are treated as non-recurring',
    ],
  },
  {
    id: '2026-03-16-installation-job-account-link',
    date: '2026-03-16',
    title: 'Installation Job Account Link',
    description:
      'The account name in the Installation Job detail page is now clickable, allowing quick navigation to the account dashboard.',
    category: 'improvement',
  },
  {
    id: '2026-02-26-accounts-table-rebuild',
    date: '2026-02-26',
    title: 'Accounts Table Upgrade',
    description:
      'The Accounts list page now features column-level filters, show/hide columns, and CSV export for faster data access.',
    category: 'improvement',
    details: [
      'Always-visible filter row with text, select, date-range, and number-range inputs per column',
      'Column visibility toggle to show/hide columns, persisted across sessions',
      'CSV download and clipboard copy for filtered data',
      'Sortable headers with arrow icons indicating active sort direction',
    ],
  },
  {
    id: '2026-02-25-rma-quote-link',
    date: '2026-02-25',
    title: 'Link RMA to Quote',
    description:
      'RMA orders can now be linked to a quote for traceability. Selecting a quote auto-populates the account and contact name.',
    category: 'feature',
    details: [
      'Searchable quote selector in the Create RMA modal',
      'Auto-fills account and contact when a quote is selected',
      'Quote number displayed on the RMA detail page and list view',
    ],
  },
  {
    id: '2026-02-16-due-date-filter',
    date: '2026-02-16',
    title: 'Due Date Filters on Tasks Board',
    description:
      'Quickly focus on what matters most with new due date filters: Due Today, Due This Week, and Due This Month. Available in both Kanban and List views.',
    category: 'feature',
    details: [
      'Filter tasks by Due Today, Due This Week, or Due This Month',
      'Works in both Kanban board and List view',
      'Combines with existing filters (project, assignee, priority, tags)',
    ],
    image: '2026-02-16-due-date-filter.png',
  },
  {
    id: '2026-02-16-sidebar-bookmarks',
    date: '2026-02-16',
    title: 'Sidebar Bookmarks',
    description:
      'Pin your most-used pages to the top of the sidebar for one-click access. Bookmarks persist across sessions and respect role-based access.',
    category: 'feature',
    details: [
      'Star icon on each nav item to toggle bookmarks (visible on hover)',
      'Bookmarks section at the top of the sidebar with quick-remove button',
      'Works in collapsed sidebar mode (icon-only bookmarks)',
      'Stored in browser localStorage — no backend changes needed',
    ],
    image: '2026-02-16-sidebar-bookmarks.png',
  },
  {
    id: '2026-02-16-procurement-csv-import',
    date: '2026-02-16',
    title: 'Procurement CSV Import into WMS Receiving',
    description:
      'Imported 367 historical purchase orders from the Procurement spreadsheet into WMS as receiving orders, providing full traceability and record-keeping for past procurement.',
    category: 'feature',
    details: [
      '367 receiving orders created with status mapping (Received, Ordered, Not Ordered Yet, Cancelled)',
      '16 suppliers linked, with 1 new supplier auto-created',
      'Staging zone (STG) and Receiving Dock (RCV) location created automatically',
      'All unmapped fields (requester, carrier, tracking, payment) preserved in order notes',
      'Historical dates preserved — no inventory generated (records only)',
    ],
  },
  {
    id: '2026-02-12-improved-account-locked-message',
    date: '2026-02-12',
    title: 'Improved Account Locked Message',
    description:
      'The login page now shows a clearer message when an account is locked, indicating it is temporary and advising users to contact their supervisor. The remaining attempts warning also now matches the actual lock threshold.',
    category: 'improvement',
  },
  {
    id: '2026-02-10-cloud-spend-cad-currency',
    date: '2026-02-10',
    title: 'Cloud Spend Now Displays All Costs in CAD',
    description:
      'All cloud spend providers (Twilio, AWS, Deepgram, OpenAI) now convert their USD billing data to CAD, matching GCP which already reported in CAD. The exchange rate is configurable via the USD_TO_CAD_RATE environment variable.',
    category: 'fix',
  },
  {
    id: '2026-02-10-qms-qd-status-change-notification',
    date: '2026-02-10',
    title: 'QMS Status Change Notifications to QD',
    description:
      'The Quality Director (QD) now receives an email notification whenever a QMS ticket status changes, ensuring full visibility over ticket lifecycle.',
    category: 'improvement',
  },
  {
    id: '2026-02-03-shippings-feature-parity',
    date: '2026-02-03',
    title: 'Shippings Page Feature Parity',
    description:
      'The React shippings page now matches the legacy AngularJS version with column-level permissions, filter persistence, Excel export, column visibility toggle, and more.',
    category: 'feature',
    details: [
      'Column-level RBAC permissions hide quote, slip, shipping data, or status columns based on user roles',
      'Filters (search, status, company, sort, page size) persist across page reloads via localStorage',
      'Export dropdown with CSV, Excel, and copy-to-clipboard options',
      'Column visibility toggle to show/hide table columns, with persistence',
      'Refresh button to reload data without a full page refresh',
      'Numbered page navigation with ellipsis for large datasets',
      'Clear all filters button to reset everything at once',
      'Unknown shipping companies are highlighted in red text',
      'French localization for company names (e.g. "Postes Canada")',
      'Full i18n integration across all shippings UI components',
    ],
  },
  {
    id: '2026-02-03-searchable-project-field-tasks',
    date: '2026-02-03',
    title: 'Searchable Project Field in Task Modal',
    description:
      'The project field in the task create/edit modal is now a searchable combobox that displays project names, making it easier to find and select the right project.',
    category: 'improvement',
    details: [
      'Project field is prefilled when editing an existing task',
      'Projects display by name instead of ID, with project number shown as secondary info',
      'Type-ahead search to quickly filter through projects',
    ],
  },
  {
    id: '2026-02-02-project-subtask-editing',
    date: '2026-02-02',
    title: 'Project Subtask Editing Support',
    description:
      'Editing a project now loads existing subtasks, preventing data loss when modifying projects with task hierarchies.',
    category: 'fix',
    details: [
      'Subtasks are now loaded when opening a project for editing',
      'Existing subtask IDs are preserved so updates apply to the correct items',
      'New subtasks added during editing are created correctly',
    ],
  },
  {
    id: '2026-02-02-sales-erp-access',
    date: '2026-02-02',
    title: 'Sales Team ERP Access',
    description:
      'Account managers can now access ERP pages including Quotes, Invoices, Sites, Locations, and Users directly from the admin panel.',
    category: 'improvement',
    details: [
      'View and manage quotes for customer proposals',
      'Access invoice records for billing visibility',
      'Review sites and locations data for account planning',
    ],
  },
  {
    id: '2026-01-30-accounts-table-account-number-column',
    date: '2026-01-30',
    title: 'Sortable Account Number Column',
    description:
      'The Organizations table now displays account numbers in a dedicated sortable column for better visibility and sorting capabilities.',
    category: 'improvement',
    details: [
      'Account number moved to its own column between Account Name and Contractual Mgr',
      'Click the "Account #" header to sort alphabetically (A-0001 → A-0999) or in reverse',
      'Account numbers displayed in monospace font for easier scanning',
    ],
    image: '2026-01-30-accounts-table-account-number-column.png',
  },
  {
    id: '2026-01-30-sites-postgresql-migration',
    date: '2026-01-30',
    title: 'Sites Page Now Uses PostgreSQL',
    description:
      'The Sites page now loads data from PostgreSQL instead of MongoDB, providing faster queries and better data consistency with the accounts system.',
    category: 'improvement',
    details: [
      'Sites list queries now run against PostgreSQL with optimized indexes',
      'Account information is joined directly, showing account name and number',
      'New sortable columns: Account Name, City, and Status',
      'Search now covers site name, address, city, postal code, and account name',
      'Filter by account using the new Account dropdown instead of Organisation',
      'All create, edit, and delete operations continue to work seamlessly',
    ],
    image: '2026-01-30-sites-postgresql-migration.png',
  },
  {
    id: '2026-01-30-invoices-postgresql-migration',
    date: '2026-01-30',
    title: 'Invoices Page Now Uses PostgreSQL',
    description:
      'The Invoices page now loads data from PostgreSQL instead of MongoDB, providing faster queries and better data consistency with the accounts system.',
    category: 'improvement',
    details: [
      'Invoice list queries now run against PostgreSQL with optimized indexes',
      'Status stats (counts and amounts) are now calculated in a single efficient query',
      'Account information is joined directly, eliminating extra lookups',
      'Manager contact info (name, email, phone) is enriched automatically',
      'All existing filters work the same way - no changes needed',
    ],
    image: '2026-01-30-invoices-postgresql-migration.png',
  },
  {
    id: '2026-01-30-kpi-management-save-fix',
    date: '2026-01-30',
    title: 'KPI Management Changes Now Save Correctly',
    description:
      'Fixed an issue where editing KPI definitions on the Manage KPIs page would appear to save but the changes would not persist.',
    category: 'fix',
    details: [
      'Changes to KPI name, description, category, frequency, and goal settings now save properly',
      'The KPI list refreshes automatically after saving to show updated values',
      'Also fixed the same issue for creating and deleting KPIs',
    ],
  },
  {
    id: '2026-01-30-kpi-period-filter-persistence',
    date: '2026-01-30',
    title: 'KPI Period Filter Remembers Your Selection',
    description:
      'The KPI Scorecard now remembers your selected period (Daily, Weekly, Monthly, etc.) so you don\'t have to reselect it each time you visit the page.',
    category: 'improvement',
  },
  {
    id: '2026-01-29-user-management-enhancements',
    date: '2026-01-29',
    title: 'Enhanced User Management',
    description:
      'The All Users page now includes delete user functionality, RBAC tags assignment, account security controls, and admin level visibility in the table.',
    category: 'feature',
    details: [
      'Delete users: Remove from organisation or delete entirely (super admins)',
      'Tags assignment: Assign RBAC tags like Account Manager, Quality Manager per organisation',
      'Account security: Force password reset checkbox and unlock locked accounts',
      'Admin Level column: See user roles at a glance with color-coded badges',
      'Navigate via Settings > Users in the admin sidebar',
    ],
  },
  {
    id: '2026-01-29-kpi-value-replacement',
    date: '2026-01-29',
    title: 'KPI Value Entry Now Replaces Previous Values',
    description:
      'Entering a KPI value for a period now replaces any existing value instead of accumulating, so the displayed value matches what you entered.',
    category: 'fix',
    details: [
      'Previously, entering 150 for a period with an existing value of 110 would show 130 (the average)',
      'Now entering 150 replaces the old value and displays 150 as expected',
      'Old values are archived (not deleted) for audit purposes',
    ],
  },
  {
    id: '2026-01-29-qms-ticket-number-search',
    date: '2026-01-29',
    title: 'QMS Search by Ticket Number',
    description:
      'The QMS Kanban search field now supports searching by ticket number (e.g., QUAL-31, CAPA-25) for faster ticket lookup.',
    category: 'improvement',
  },
  {
    id: '2026-01-29-kpi-period-filter-fix',
    date: '2026-01-29',
    title: 'KPI Scorecard Period Filter Fix',
    description:
      'Fixed an issue where KPIs with different frequencies were incorrectly showing across all period views. Weekly KPIs now only appear in weekly view, monthly in monthly view, etc.',
    category: 'fix',
    details: [
      'KPIs are now filtered by their frequency to match the selected period type',
      'Weekly KPIs (e.g., "Account Payable") no longer appear in Monthly view',
      'Switch between period types to see KPIs of each frequency',
    ],
  },
  {
    id: '2026-01-29-kpi-past-period-editing',
    date: '2026-01-29',
    title: 'KPI Past Period Editing with Unlock Protection',
    description:
      'Past KPI periods can now be edited after unlocking, allowing corrections to historical data while protecting against accidental changes.',
    category: 'improvement',
    details: [
      'Past period cells show a lock icon - click to unlock for editing',
      'Unlocked cells display an unlock icon with amber highlight for visibility',
      'Unlock state persists for the session to allow multiple edits',
      'Current and future periods remain directly editable as before',
    ],
  },
  {
    id: '2026-01-24-transmitter-connections-export',
    date: '2026-01-24',
    title: 'Transmitter Connections Export',
    description:
      'Account managers can now export transmitter connection data directly from the Account Dashboard to quickly identify which sensors are connected via gateway, WiFi, or LTE.',
    category: 'feature',
    details: [
      'New "Export Connections" button in the Sensors tab header',
      'Downloads CSV with connection type for each transmitter (Gateway, WiFi, LTE, LoRaWAN)',
      'Includes gateway MAC, signal strength (RSSI), WiFi SSID, and SIM provider details',
      'Shows associated sensors, site, and location for each transmitter',
      'Helps respond to customer inquiries about their infrastructure connectivity',
    ],
  },
  {
    id: '2026-01-24-gcp-cloud-spend-monitoring',
    date: '2026-01-24',
    title: 'GCP Cloud Spend Monitoring',
    description:
      'New Cloud Spend page in Settings to monitor GCP billing costs with real-time data from BigQuery billing export.',
    category: 'feature',
    details: [
      'View current month vs previous month spend comparison with percentage change',
      'Cost breakdown by GCP service (Compute Engine, Cloud Storage, BigQuery, etc.)',
      'Cost breakdown by GCP project',
      'Daily spend trend chart for the last 30 days',
      'Projected monthly total based on current spending rate',
      'Auto-refreshes every 5 minutes for up-to-date data',
    ],
  },
  {
    id: '2026-01-21-qms-ticket-creation-notifications',
    date: '2026-01-21',
    title: 'QMS Ticket Creation Email Notifications',
    description:
      'The Quality Director and ticket creators now receive automatic email notifications when new QMS tickets or CAPAs are created.',
    category: 'feature',
    details: [
      'Quality Director (QD) receives notification for every new ticket created',
      'Ticket creator receives a confirmation email with ticket details',
      'Email includes ticket ID, title, type, priority, description preview, and direct link to the ticket',
      'Supports both English and French based on user language preference',
    ],
  },
  {
    id: '2026-01-21-qms-assign-permission',
    date: '2026-01-21',
    title: 'QMS Assign Permission',
    description:
      'New permission to control who can assign QMS tickets and CAPAs to users, providing finer-grained access control over ticket assignment.',
    category: 'feature',
    details: [
      'New "Assign" permission in QMS Settings for admins to toggle per user',
      'Users without permission see the assignee field as disabled in QD Approval panel',
      'Assignment prompt is auto-skipped when changing status to "In Progress" for users without permission',
      'Helps organizations restrict ticket assignment to designated coordinators',
    ],
  },
  {
    id: '2026-01-21-accounts-list-table',
    date: '2026-01-21',
    title: 'Accounts List Table View',
    description:
      'The Organizations page now displays accounts in a sortable data table with enriched columns for better visibility into account status, contracts, and revenue.',
    category: 'feature',
    details: [
      'New table layout replaces card-based view for faster scanning of accounts',
      'Sortable columns: Account Name, Contractual Manager, Contract #, Status, Sensors, Calibrations Due, Contract Dates, Years, ARR',
      'Calibrations Due shows count of certificates expiring within 90 days with warning indicator',
      'ARR (Annual Recurring Revenue) column shows formatted currency values',
      'Contract information includes start date, end date, and calculated contract duration in years',
      'Click any row to navigate to the account detail page',
    ],
    image: '2026-01-21-accounts-list-table.png',
  },
  {
    id: '2026-01-20-qms-assignment-prompt',
    date: '2026-01-20',
    title: 'QMS Assignment Prompt on Status Change',
    description:
      'When moving an unassigned QMS ticket to "In Progress" status, users are now prompted to optionally assign someone before proceeding.',
    category: 'improvement',
    details: [
      'Prompt appears when dragging an unassigned ticket to the "In Progress" column',
      'Also triggers when changing status via the ticket form dropdown',
      'Users can assign someone and continue, or skip to proceed without assignment',
      'Helps ensure tickets are properly assigned when work begins',
    ],
  },
  {
    id: '2026-01-20-account-dashboard-sales-insights',
    date: '2026-01-20',
    title: 'Account Dashboard Sales Insights',
    description:
      'The Account Dashboard Overview tab now displays key sales metrics and actionable insights to help sales teams quickly identify account opportunities.',
    category: 'feature',
    details: [
      'New revenue metrics: Win Rate, Weighted Pipeline, and Won YTD displayed prominently',
      'Contract renewal alert with days-until-expiry countdown and "Create Renewal Opportunity" action button',
      'Deals Needing Attention card showing deals closing soon, stalled deals, and high-priority deals',
      'Quote Pipeline card with visual funnel showing preparation, sent, and approved stages',
      'Recent Activity card with engagement level indicator and suggested next actions',
    ],
    image: '2026-01-20-account-dashboard-sales-insights.png',
  },
  {
    id: '2026-01-20-qms-ceo-approval-notifications',
    date: '2026-01-20',
    title: 'QMS CEO Approval Email Notifications',
    description:
      'The CEO now receives automatic email notifications when QMS tickets require executive-level review due to high priority or critical severity.',
    category: 'feature',
    details: [
      'Auto-notification when QD approves tickets with Priority 5 (Highest) or Severity Level 2 (Critical)',
      'Email includes ticket details, priority/severity badges, escalation reason, and direct link to review',
      'Also triggered for manual CEO escalations',
      'Bilingual template support (English/French)',
    ],
  },
  {
    id: '2026-01-20-qms-permissions-admin',
    date: '2026-01-20',
    title: 'QMS Permissions Admin UI',
    description:
      'Full admins can now manage QMS permissions for users directly from a new settings page, enabling granular control over who can approve tickets, perform CEO review, and more.',
    category: 'feature',
    details: [
      'New Settings page accessible via gear icon in QMS board header (full admin only)',
      'Search users by name or email',
      'Toggle permissions: QD Approval, CEO Review, Create, Edit, Delete',
      'Real-time updates with optimistic UI and toast notifications',
      'Permission descriptions displayed for clarity',
    ],
  },
  {
    id: '2026-01-20-qms-attachments-on-create',
    date: '2026-01-20',
    title: 'QMS Attachments During Ticket Creation',
    description:
      'File attachments can now be added while creating new QMS tickets, rather than only after the ticket exists.',
    category: 'feature',
    details: [
      'Select files via drag-and-drop or file picker during ticket creation',
      'Preview images and view file details before submitting',
      'Files are uploaded automatically after the ticket is created',
      'Upload progress tracking with success/error status per file',
      'Works in both the wizard and modal ticket creation flows',
    ],
  },
  {
    id: '2026-01-20-qms-email-notifications',
    date: '2026-01-20',
    title: 'QMS Email Notifications',
    description:
      'Ticket creators and assignees now receive email notifications when comments are added, investigation fields are updated, or ticket status changes.',
    category: 'feature',
    details: [
      'Email notifications when comments are added to tickets',
      'Notifications when "Analyse de Cause Racine" or "Résultats de l\'Investigation" fields are updated',
      'Notifications when tickets are moved between columns (status changes)',
      'Emails are sent to the ticket creator and assignee (excluding the person who made the change)',
      'Supports English and French based on user language preferences',
    ],
  },
  {
    id: '2026-01-20-qms-kanban-drag-drop-fix',
    date: '2026-01-20',
    title: 'QMS Kanban Drag and Drop Fix',
    description:
      'Fixed an issue where QMS tickets could not be dragged to another column when dropping onto an existing ticket card.',
    category: 'fix',
    details: [
      'Tickets can now be dropped on other cards or empty column space',
      'Fixed optimistic update for empty target columns',
      'Added same-column detection to prevent unnecessary API calls',
    ],
  },
  {
    id: '2026-01-20-timezone-standardization',
    date: '2026-01-20',
    title: 'Timezone Handling Standardization',
    description:
      'Standardized timezone handling across the platform with UTC as the default for new accounts and consistent period calculations.',
    category: 'improvement',
    details: [
      'New @atek/timezone package provides consistent period calculations (daily, weekly, monthly, quarterly, annually)',
      'KPI scorecard periods now correctly respect timezone boundaries',
      'Account health metrics and QMS trend analysis use correct date boundaries',
      'Daily log reports now use correct month boundaries for business logic',
      'Database defaults set to UTC for new accounts (existing accounts retain their configured timezone)',
      'Improved accuracy for reports and metrics that span timezone boundaries',
    ],
  },
  {
    id: '2026-01-20-kpi-scorecard-enhancements',
    date: '2026-01-20',
    title: 'KPI Scorecard Enhancements',
    description:
      'Major improvements to the KPI Scorecard with inline editing, visual status indicators, and a completion tracking widget for faster data entry.',
    category: 'feature',
    details: [
      'Inline cell editing: Click any cell to enter values directly (Enter to save, Escape to cancel, Tab to move)',
      'Table view with spreadsheet-style layout showing KPIs as rows and periods as columns',
      'Visual status indicators: green for on-track, red for off-track, yellow dashed border for missing overdue values',
      'Completion tracking widget: Shows org-wide KPI completion percentage with breakdown by owner',
      'Optimistic updates: Values appear instantly while saving in the background',
      'Toggle between table view (for data entry) and card view (for overview)',
    ],
  },
  {
    id: '2026-01-20-organisation-search',
    date: '2026-01-20',
    title: 'Searchable Organisation Dropdown',
    description:
      'The organisation dropdown in Customer Onboarding now includes a search field for quickly finding organisations by name or number.',
    category: 'improvement',
    details: [
      'Type to filter organisations by name or org number',
      'Shows "No organisation found" when search yields no results',
      'Checkmark indicator shows currently selected organisation',
    ],
    image: '2026-01-20-organisation-search.png',
  },
  {
    id: '2026-01-20-calibration-export-csv-fix',
    date: '2026-01-20',
    title: 'Calibration Certificates CSV Export Fix',
    description:
      'Fixed an issue where the CSV export button on the Calibration Certificates page was downloading HTML instead of CSV data.',
    category: 'fix',
  },
  {
    id: '2026-01-19-calibration-certificates-filters',
    date: '2026-01-19',
    title: 'Enhanced Calibration Certificates Filtering',
    description:
      'The calibration certificates page now includes date range filters and a new status option for certificates due after 90 days.',
    category: 'improvement',
    details: [
      'New date range filters (Due From / Due To) for precise date filtering',
      'New "Later (90+ days)" status filter for certificates expiring beyond 90 days',
      'New summary card showing count of certificates due after 90 days',
      'Improved grid layout to accommodate the additional summary card',
    ],
  },
  {
    id: '2026-01-19-sim-card-inventory',
    date: '2026-01-19',
    title: 'SIM Card Inventory Management',
    description:
      'New SIM inventory page to track and manage cellular IoT SIM cards synced from Jasper (Bell) platform.',
    category: 'feature',
    details: [
      'Real-time sync of ~200 SIM cards from Jasper IoT platform',
      'Summary cards showing active, inactive, suspended, and unassigned SIMs',
      'Search by ICCID, MSISDN, IMEI, or customer name',
      'Filter by status and assignment state',
      'Manual account assignment for SIMs without org_num mapping',
      'Usage tracking with cycle-to-date data in MB',
    ],
  },
  {
    id: '2026-01-19-qms-ticket-attachments',
    date: '2026-01-19',
    title: 'QMS Ticket File Attachments',
    description:
      'QMS tickets now support file attachments, allowing users to upload images, documents, and other files directly to tickets.',
    category: 'feature',
    details: [
      'Upload multiple files at once (images, PDFs, Office documents, and more)',
      'File size limit of 50MB per file',
      'Download attachments with a single click',
      'Soft delete with audit trail for compliance',
      'Files stored securely in Google Cloud Storage',
    ],
  },
  {
    id: '2026-01-19-qms-shareable-ticket-urls',
    date: '2026-01-19',
    title: 'Shareable QMS Ticket URLs',
    description:
      'QMS tickets now have unique URLs that can be shared with team members for direct access to specific tickets.',
    category: 'feature',
    details: [
      'Clicking a ticket updates the URL to /qms/{ticketId}',
      'URLs can be copied and shared to link directly to a ticket',
      'Opening a shared link automatically displays the ticket modal',
      'Filter parameters are preserved when navigating between tickets',
    ],
  },
  {
    id: '2026-01-19-project-roadblock-reason',
    date: '2026-01-19',
    title: 'Project Roadblock Reason Tracking',
    description:
      'Projects marked with Roadblock status now require and display a reason, providing better visibility into why work is blocked.',
    category: 'feature',
    details: [
      'New "Roadblock Reason" field appears when status is set to Roadblock',
      'Reason is required before saving a project with Roadblock status',
      'Roadblock reason prominently displayed in project details panel',
    ],
  },
  {
    id: '2026-01-19-kanban-drag-drop-fix',
    date: '2026-01-19',
    title: 'Kanban Drag and Drop Instant Update',
    description:
      'Fixed an issue where the Kanban board required a page refresh to see task changes after drag and drop.',
    category: 'fix',
  },
  {
    id: '2026-01-19-project-tasks-in-details',
    date: '2026-01-19',
    title: 'Project Tasks Display in Details',
    description:
      'Tasks created through the project form are now displayed in the project details panel for complete visibility.',
    category: 'improvement',
    details: [
      'New Tasks section in project details showing all linked tasks',
      'Tasks display title, description, and current status',
      'Supports tasks added during project creation or editing',
    ],
  },
  {
    id: '2026-01-19-operations-calendar-improvements',
    date: '2026-01-19',
    title: 'Operations Calendar Improvements',
    description:
      'Streamlined the operations calendar view and added project activity tracking for better visibility into workflow changes.',
    category: 'improvement',
    details: [
      'Removed milestone clutter from calendar - workflow steps now tracked in project activity log',
      'New Activity Log section in project details showing all changes chronologically',
      'Improved calendar event titles: now display "Quote Number - Account Name" format',
      'Milestone completions, status changes, and reschedules are now logged with timestamps and user info',
    ],
  },
  {
    id: '2026-01-18-qms-account-selection',
    date: '2026-01-18',
    title: 'QMS Account Selection in Wizards',
    description:
      'Added account selection capability to QMS wizards for better organization of quality management tasks.',
    category: 'feature',
    details: [
      'Select target account when creating QMS tickets',
      'Filter wizards by account context',
      'Improved workflow for multi-account environments',
    ],
  },
  {
    id: '2026-01-15-wms-shipping',
    date: '2026-01-15',
    title: 'Warehouse Shipping Management',
    description:
      'New shipping module in WMS for managing outbound logistics and carrier integrations.',
    category: 'feature',
    details: [
      'Create and track shipments',
      'Generate shipping labels',
      'Carrier rate comparison',
      'Shipment history and analytics',
    ],
  },
  {
    id: '2026-01-10-intelligence-dashboard',
    date: '2026-01-10',
    title: 'Enhanced Intelligence Dashboard',
    description:
      'Improved customer health scoring and alert visualization in the Intelligence module.',
    category: 'improvement',
    details: [
      'New health score calculation algorithm',
      'Real-time alert notifications',
      'Exportable reports',
    ],
  },
  {
    id: '2026-01-05-sensor-status-display',
    date: '2026-01-05',
    title: 'Sensor Status Display Fix',
    description:
      'Fixed an issue where sensor status was not displaying correctly in certain views.',
    category: 'fix',
  },
  {
    id: '2024-12-20-technician-portal',
    date: '2024-12-20',
    title: 'Technician Portal Launch',
    description:
      'New mobile-friendly portal for field technicians to access job checklists and access codes.',
    category: 'feature',
    details: [
      'Mobile-optimized interface',
      'QR code scanning for equipment',
      'Offline capability for remote locations',
      'Real-time job status updates',
    ],
  },
  {
    id: '2024-12-15-performance-improvements',
    date: '2024-12-15',
    title: 'Dashboard Performance Improvements',
    description:
      'Optimized data loading and rendering for faster dashboard performance.',
    category: 'improvement',
    details: [
      '50% faster initial page load',
      'Improved caching strategy',
      'Reduced API calls with smart batching',
    ],
  },
];
